import { type EpisodeSummary } from '@audacy-clients/client-services/src/dataServices/DataServices';
import isDate from 'lodash/isDate';
import { atom, atomFamily, useRecoilValue } from 'recoil';
import {
  noCacheDefaultSelector,
  noCacheDefaultSelectorFamily,
} from './helpers/noCacheDefaultSelector';

export enum DownloadStatus {
  Default = 'default',
  Pending = 'pending',
  Downloading = 'downloading',
  Completed = 'completed',
}

export type CompletedDownloadItem = {
  status: DownloadStatus;
  episode: EpisodeSummary;
  localUri: string;
};

export type DownloadQueueItem = {
  progress?: number;
  status?: DownloadStatus;
  episode?: EpisodeSummary;
};

export const downloadQueueItemState = atomFamily<DownloadQueueItem | null, string>({
  key: 'DownloadQueueItem',
  default: null,
});

export const completedDownloadsState = atom<Map<string, CompletedDownloadItem>>({
  key: 'CompletedDownloads',
  default: new Map(),
});

export const downloadItemSelectorFamily = noCacheDefaultSelectorFamily<
  CompletedDownloadItem | DownloadQueueItem | undefined | null,
  { id?: string; fromCompletedDownloads?: boolean }
>({
  get({ id, fromCompletedDownloads }) {
    return ({ get }) => {
      if (!id) {
        return;
      }
      if (fromCompletedDownloads) {
        const completedDownloadsItems = get(completedDownloadsState);
        return completedDownloadsItems.get(id);
      }

      const downloadQueueItem = get(downloadQueueItemState(id));
      const completedDownloadsItems = get(completedDownloadsState);
      return completedDownloadsItems.get(id) ?? downloadQueueItem;
    };
  },
  key: 'downloadItemSelectorFamily',
});

export const downloadItemsSelector = noCacheDefaultSelector({
  get: ({ get }) => {
    const completedDownloadsItems = get(completedDownloadsState);
    const episodes = Array.from(completedDownloadsItems.values()).map((item) => {
      const publishDate = item.episode.publishDate as string | Date;

      return {
        ...item.episode,
        publishDate: isDate(publishDate) ? publishDate : new Date(publishDate),
      };
    });
    return episodes;
  },
  key: 'downloadItemsSelector',
});

export const useDownloadItems = () => useRecoilValue(downloadItemsSelector);
